import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Bottom sheet on mobile (drag the grabber down to close), side panel or centered dialog on desktop.
 * Escape and backdrop close it; focus moves inside on open and returns on close.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  variant = 'side',
  full = false,
  headerActions,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  variant?: 'side' | 'center';
  full?: boolean;
  headerActions?: ReactNode;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ y: number; dy: number } | null>(null);
  // Parents often pass a new onClose on every render: keep the latest in a ref so the
  // open/focus effect below runs once per opening and never steals focus while typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCloseRef.current();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      const target = ref.current?.querySelector<HTMLElement>('[data-autofocus]') ?? ref.current;
      target?.focus({ preventScroll: true });
    });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previous?.focus?.({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { y: e.clientY, dy: 0 };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !ref.current) return;
    drag.current.dy = Math.max(0, e.clientY - drag.current.y);
    ref.current.style.transition = 'none';
    ref.current.style.transform = `translateY(${drag.current.dy}px)`;
  };
  const onPointerUp = () => {
    if (!drag.current || !ref.current) return;
    const close = drag.current.dy > 110;
    ref.current.style.transition = 'transform 0.3s var(--ease)';
    ref.current.style.transform = close ? 'translateY(100%)' : '';
    drag.current = null;
    if (close) setTimeout(() => onCloseRef.current(), 180);
  };

  return createPortal(
    <>
      <div className={`sheet-backdrop sheet-backdrop-${variant}`} onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        className={`sheet sheet-${variant}${full ? ' sheet-full' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        <div className="sheet-grab" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} />
        {(title || headerActions) && (
          <div className="sheet-head">
            {title && <h2 id={labelledBy}>{title}</h2>}
            {headerActions}
          </div>
        )}
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </>,
    document.body,
  );
}
